import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import {
  deleteSupport,
  getMySupports,
  restoreMySupport,
} from "../../../services/SupportService";
import {
  getSupportPriorityDisplay,
  getSupportSubcategoryLabel,
  getSupportTypeMeta,
} from "../../../contants/supportTypes";
import { toast } from "react-toastify";
import "./archivedSupportRequests.css";

const STATUS_LABELS = {
  pending: "Pending",
  in_progress: "In progress",
  resolved: "Resolved",
};

const PRIORITY_LABELS = {
  faible: "Low",
  moyenne: "Medium",
  haute: "High",
  critique: "Critical",
};

const formatTicketRef = (id) => `#${String(id).slice(-6).toUpperCase()}`;

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const getTicketSubject = (support) => {
  const subLabel = getSupportSubcategoryLabel(
    support.type,
    support.subcategory,
    support.customSubcategory
  );
  if (subLabel) return subLabel;
  if (support.type === "other" && support.customType?.trim()) {
    return support.customType.trim();
  }
  return getSupportTypeMeta(support.type)?.label || "Support request";
};

const getTicketCategory = (support) => {
  if (support.type === "other" && support.customType?.trim()) return "Other";
  return getSupportTypeMeta(support.type)?.label || support.type || "—";
};

const ArchivedSupportRequests = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [supports, setSupports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [restoringId, setRestoringId] = useState("");
  const [deletingId, setDeletingId] = useState("");

  const loadArchived = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getMySupports({ archived: true });
      setSupports(response.supports || []);
    } catch (error) {
      toast.error(error.message || t("archivedSupportLoadError"));
      setSupports([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadArchived();
  }, [loadArchived]);

  const filteredSupports = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return supports;

    return supports.filter((support) => {
      const subject = getTicketSubject(support).toLowerCase();
      const category = getTicketCategory(support).toLowerCase();
      const ref = formatTicketRef(support._id).toLowerCase();
      const description = (support.description || "").toLowerCase();
      return (
        subject.includes(query) ||
        category.includes(query) ||
        ref.includes(query) ||
        description.includes(query) ||
        String(support._id).toLowerCase().includes(query)
      );
    });
  }, [supports, search]);

  const handleRestore = async (support) => {
    const result = await Swal.fire({
      title: t("archivedSupportRestoreConfirm"),
      text: t("archivedSupportRestoreConfirmText"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("archivedSupportRestoreAction"),
      cancelButtonText: t("cancel"),
      confirmButtonColor: "#16a34a",
    });
    if (!result.isConfirmed) return;

    setRestoringId(support._id);
    try {
      const response = await restoreMySupport(support._id);
      setSupports((prev) => prev.filter((item) => item._id !== support._id));
      toast.success(response.message || t("archivedSupportRestored"));
    } catch (error) {
      toast.error(error.message || t("archivedSupportRestoreError"));
    } finally {
      setRestoringId("");
    }
  };

  const handleDelete = async (support) => {
    const result = await Swal.fire({
      title: t("archivedSupportDeleteConfirm"),
      text: t("archivedSupportDeleteConfirmText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("delete"),
      cancelButtonText: t("cancel"),
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;

    setDeletingId(support._id);
    try {
      const response = await deleteSupport(support._id);
      setSupports((prev) => prev.filter((item) => item._id !== support._id));
      toast.success(response.message || t("archivedSupportDeleted"));
    } catch (error) {
      toast.error(error.message || t("archivedSupportDeleteError"));
    } finally {
      setDeletingId("");
    }
  };

  return (
    <>
      <PageTitle motherMenu={t("Archived")} activeMenu={t("navArchivedSupportRequests")} />
      <div className="asr-page">
        <header className="asr-hero">
          <div className="asr-hero__text">
            <p className="asr-hero__eyebrow">{t("Archived")}</p>
            <h1 className="asr-hero__title">{t("archivedSupportPageTitle")}</h1>
            <p className="asr-hero__subtitle">{t("archivedSupportPageSubtitle")}</p>
          </div>
          <Link to="/my-support-requests" className="asr-hero__link">
            <i className="fas fa-arrow-left" aria-hidden="true" />
            {t("archivedSupportBackToRequests")}
          </Link>
        </header>

        <div className="asr-toolbar">
          <label className="asr-search">
            <i className="fas fa-search" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("archivedSupportSearchPlaceholder")}
            />
          </label>
          <span className="asr-count">
            {filteredSupports.length}{" "}
            {filteredSupports.length === 1
              ? t("archivedSupportCountOne")
              : t("archivedSupportCountMany")}
          </span>
        </div>

        <div className="asr-card">
          {loading ? (
            <div className="asr-empty">
              <div className="asr-spinner" aria-hidden="true" />
              <p>{t("archivedSupportLoading")}</p>
            </div>
          ) : filteredSupports.length === 0 ? (
            <div className="asr-empty">
              <div className="asr-empty__icon" aria-hidden="true">
                <i className="fas fa-box-open" />
              </div>
              <h3>{t("archivedSupportEmptyTitle")}</h3>
              <p>{t("archivedSupportEmptyText")}</p>
            </div>
          ) : (
            <div className="asr-table-wrap">
              <table className="asr-table">
                <thead>
                  <tr>
                    <th>{t("archivedSupportColRef")}</th>
                    <th>{t("archivedSupportColSubject")}</th>
                    <th>{t("archivedSupportColPriority")}</th>
                    <th>{t("archivedSupportColStatus")}</th>
                    <th>{t("archivedSupportColDate")}</th>
                    <th>{t("archivedSupportColActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSupports.map((support) => {
                    const status = support.status || "pending";
                    const priority = support.priority || "moyenne";
                    const busy =
                      restoringId === support._id || deletingId === support._id;

                    return (
                      <tr key={support._id}>
                        <td>
                          <span className="asr-ref">{formatTicketRef(support._id)}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="asr-subject"
                            onClick={() =>
                              navigate(`/my-support-requests/${support._id}`)
                            }
                          >
                            <strong>{getTicketSubject(support)}</strong>
                            <span>{getTicketCategory(support)}</span>
                          </button>
                        </td>
                        <td>
                          <span className={`asr-pill asr-pill--priority-${priority}`}>
                            {PRIORITY_LABELS[priority] ||
                              getSupportPriorityDisplay(priority)}
                          </span>
                        </td>
                        <td>
                          <span className={`asr-pill asr-pill--status-${status}`}>
                            {STATUS_LABELS[status] || status}
                          </span>
                        </td>
                        <td className="asr-date">{formatDate(support.updatedAt || support.createdAt)}</td>
                        <td>
                          <div className="asr-actions">
                            <button
                              type="button"
                              className="asr-action asr-action--open"
                              title={t("archivedSupportOpenAction")}
                              aria-label={t("archivedSupportOpenAction")}
                              onClick={() =>
                                navigate(`/my-support-requests/${support._id}`)
                              }
                            >
                              <i className="fas fa-up-right-from-square" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="asr-action asr-action--restore"
                              title={t("archivedSupportRestoreAction")}
                              aria-label={t("archivedSupportRestoreAction")}
                              disabled={busy}
                              onClick={() => handleRestore(support)}
                            >
                              <i className="fas fa-undo" aria-hidden="true" />
                              {restoringId === support._id
                                ? t("archivedSupportRestoring")
                                : t("archivedSupportRestoreAction")}
                            </button>
                            <button
                              type="button"
                              className="asr-action asr-action--delete"
                              title={t("delete")}
                              aria-label={t("delete")}
                              disabled={busy}
                              onClick={() => handleDelete(support)}
                            >
                              <i className="fas fa-trash" aria-hidden="true" />
                              {deletingId === support._id
                                ? t("archivedSupportDeleting")
                                : t("delete")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ArchivedSupportRequests;
